import React, { forwardRef } from "react";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "outline" | "ghost" | "danger" | "accent" | "success" 
    // Material UI compatibility aliases
    | "contained" | "text" | "outlined";
  size?: "xs" | "sm" | "md" | "lg";
  loading?: boolean;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

/**
 * Arc-style Button component
 * 
 * Features:
 * - Multiple variants: primary, secondary, outline, ghost, danger, accent, success
 * - Multiple sizes: xs, sm, md, lg
 * - Loading state with spinner
 * - Icon support (left and right)
 * - Full width option
 * - Smooth transitions and hover effects
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      loading = false,
      disabled = false,
      fullWidth = false,
      leftIcon,
      rightIcon,
      children,
      className = "",
      type = "button",
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    // Base styles - Arc-style design
    const baseStyles = `
      inline-flex items-center justify-center
      font-semibold
      rounded-arc-md
      transition-all duration-arc-normal ease-arc
      focus:outline-none
      focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-accent
      disabled:cursor-not-allowed disabled:opacity-50
      select-none
    `;

    // Size styles
    const sizeStyles = {
      xs: "px-2.5 py-1 text-xs gap-1 rounded-arc-sm",
      sm: "px-3 py-1.5 text-sm gap-1.5 rounded-arc-sm",
      md: "px-4 py-2 text-sm gap-2 rounded-arc-md",
      lg: "px-6 py-3 text-base gap-2.5 rounded-arc-md",
    };

    // Variant styles - Arc-style design with new color tokens
    const variantStyles = {
      // Primary - Accent color (indigo)
      primary: `
        bg-accent text-white
        shadow-arc-sm
        hover:bg-accent-light hover:shadow-arc-md hover:-translate-y-0.5
        active:translate-y-0 active:shadow-arc-sm active:scale-[0.98]
        disabled:bg-content-tertiary disabled:shadow-none disabled:translate-y-0
      `,
      // Accent - Same as primary but with glow effect
      accent: `
        bg-accent text-white
        shadow-arc-accent
        hover:bg-accent-light hover:shadow-arc-accent-lg hover:-translate-y-0.5
        active:translate-y-0 active:shadow-arc-accent active:scale-[0.98]
        disabled:bg-content-tertiary disabled:shadow-none disabled:translate-y-0
      `,
      // Secondary - Surface background
      secondary: `
        bg-surface-secondary text-content-primary
        border border-arc-border
        hover:bg-surface-tertiary hover:border-arc-border-strong
        active:scale-[0.98]
        disabled:bg-surface-tertiary disabled:text-content-tertiary disabled:border-arc-border
      `,
      // Outline - Transparent with border
      outline: `
        bg-transparent text-content-primary
        border-2 border-arc-border
        hover:bg-surface-secondary hover:border-arc-border-strong
        active:scale-[0.98]
        disabled:text-content-tertiary disabled:border-arc-border
      `,
      // Ghost - No background
      ghost: `
        bg-transparent text-content-secondary
        hover:bg-surface-secondary hover:text-content-primary
        active:bg-surface-tertiary active:scale-[0.98]
        disabled:text-content-tertiary
      `,
      // Danger - Destructive actions
      danger: `
        bg-error text-white
        shadow-arc-sm
        hover:bg-error-dark hover:shadow-arc-md hover:-translate-y-0.5
        active:translate-y-0 active:shadow-arc-sm active:scale-[0.98]
        disabled:bg-content-tertiary disabled:shadow-none disabled:translate-y-0
      `,
      // Success - Confirmation actions
      success: `
        bg-success text-white
        shadow-arc-sm
        hover:bg-success-dark hover:shadow-arc-md hover:-translate-y-0.5
        active:translate-y-0 active:shadow-arc-sm active:scale-[0.98]
        disabled:bg-content-tertiary disabled:shadow-none disabled:translate-y-0
      `,
      // Material UI compatibility aliases
      contained: `
        bg-accent text-white
        shadow-arc-sm
        hover:bg-accent-light hover:shadow-arc-md hover:-translate-y-0.5
        active:translate-y-0 active:shadow-arc-sm active:scale-[0.98]
        disabled:bg-content-tertiary disabled:shadow-none disabled:translate-y-0
      `,
      text: `
        bg-transparent text-content-secondary
        hover:bg-surface-secondary hover:text-content-primary
        active:bg-surface-tertiary active:scale-[0.98]
        disabled:text-content-tertiary
      `,
      outlined: `
        bg-transparent text-content-primary
        border-2 border-arc-border
        hover:bg-surface-secondary hover:border-arc-border-strong
        active:scale-[0.98]
        disabled:text-content-tertiary disabled:border-arc-border
      `,
    };

    // Loading spinner - Arc-style
    const LoadingSpinner = () => (
      <svg
        className="animate-arc-spin h-4 w-4"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="3"
        />
        <path
          className="opacity-90"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    );

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        className={`
          ${baseStyles}
          ${sizeStyles[size]}
          ${variantStyles[variant]}
          ${fullWidth ? "w-full" : ""}
          ${className}
        `}
        {...props}
      >
        {loading ? (
          <>
            <LoadingSpinner />
            <span className={loading ? "opacity-80" : ""}>{children}</span>
          </>
        ) : (
          <>
            {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
            {children}
            {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = "Button";

export { Button };
export default Button;
